import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, of, catchError, throwError, Observable } from 'rxjs';
import { exhaustMap, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { UserService } from './user.service';
import { LanguageCulture, Result } from '../autogenerated/model.autogenerated';
import { ApiService } from './api.service';
import * as moment from 'moment-timezone';
import { DateAdapter } from '@angular/material/core';


@Injectable({
  providedIn: 'root',
})
export class LanguageService {

  private _currentLanguageSubject = new BehaviorSubject<LanguageCulture | null>(null);
  private _supportedLanguages: LanguageCulture[] = [];

  private _ianaName: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

  private regex = /^[0-9]+$/g
  private regexSeparator = /\./g
  private _decimalSeparatorIt = ',';
  private _thousandSeparatorIt = '.';
  private _decimalSeparatorEn = '.';
  private _thousandSeparatorEn = ',';
  private _decimalSeparator: string = '';
  private _thousandSeparator: string = '';


  public get currentLanguage$() { return this._currentLanguageSubject; }
  public get currentLanguage() { return this._currentLanguageSubject.value; }
  public get supportedLanguages() { return this._supportedLanguages; }
  public get decimalSeparator() { return this._decimalSeparator; }
  public get thousandSeparator() { return this._thousandSeparator; }

  constructor(
    private readonly _translate: TranslateService,
    private readonly _api: ApiService,
    private readonly _user: UserService,
    private readonly _dateAdapter: DateAdapter<Date>,

  ) {

  }

  public initialize(userLanguage: LanguageCulture | null): Observable<LanguageCulture> {
    return this._api.LanguageCulture_List()
      .pipe(
        tap(x => this._supportedLanguages = x.items!),
        map(x => {
          if (userLanguage != null && userLanguage.languageCultureName != null) {
            return userLanguage.languageCultureName;
          } else {
            return this._getFallbackLanguageCultureName();
          }
        }),
        map(x => this._checkAndGetLanguageCulture(x)),
        exhaustMap(language => {
          if (userLanguage == null || language.languageCultureName != userLanguage.languageCultureName) {
            return this._updateUserLanguage(language)
              .pipe(
                map(x => language)
              );
          } else {
            return of(null)
              .pipe(
                map(x => language)
              );
          }
        }),
        tap(language => this._setUserLanguage(language)),
        catchError((err, obs) => {
          var lang = new LanguageCulture();
          lang.languageCultureName = environment.DEFAULT_LANGUAGE;
          lang.idLanguageCulture = this._supportedLanguages.find(x => x.languageCultureName == environment.DEFAULT_LANGUAGE)?.idLanguageCulture;
          this._setUserLanguage(lang);
          return of(lang);
        })
      );
  }
  private _getFallbackLanguageCultureName(): string {
    let currentLanguage = localStorage.getItem('locale');
    return currentLanguage ?? navigator.language;
  }

  public now() {
    return moment.tz(this._ianaName);
  }

  public dateNowTime(date: moment.Moment) {
    let now = this.now();
    now = now.set('date', date.date());
    now = now.set('month', date.month());
    now = now.set('year', date.year());
    return now;
  }

  public setUserLanguage(languageCulture: LanguageCulture) {
    languageCulture = this._checkAndGetLanguageCulture(languageCulture.languageCultureName!);
    return this._updateUserLanguage(languageCulture)
      .pipe(
        tap(x => this._setUserLanguage(languageCulture))
      );
  }

  private _setUserLanguage(languageCulture: LanguageCulture) {
    moment.locale(languageCulture.languageCultureName?.substring(0, 2));
    this._dateAdapter.setLocale(languageCulture.languageCultureName);
    this._dateAdapter.setLocale(languageCulture.languageCultureName);
    this._currentLanguageSubject.next(languageCulture);
    this._translate.use(languageCulture.languageCultureName!);
    this._setRegex(languageCulture);

    let currentLanguage = localStorage.getItem('locale');
    if (currentLanguage != languageCulture.languageCultureName) {
      localStorage.setItem('locale', languageCulture.languageCultureName!);
      window.location.reload();
    }
  }

  private _checkAndGetLanguageCulture(languageCultureName: string) {
    let languageCulture = this._findLanguageCulture(languageCultureName);

    if (languageCulture == null) {
      console.warn(`Language - unsupported language ${languageCultureName}`);
      languageCulture = this._findLanguageCulture(environment.DEFAULT_LANGUAGE);
    }

    console.info(`Language - setting user language to ${languageCultureName}`);

    if (languageCulture == null) {
      const errorMessage = `Language - error unable to find default language ${languageCultureName}`;
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    return languageCulture;
  }

  private _updateUserLanguage(languageCulture: LanguageCulture) {
    let language = this._findLanguageCulture(environment.DEFAULT_LANGUAGE);
    if (this._user.idUser == null) {
      this._currentLanguageSubject.next(language ?? null);
      this._translate.use(environment.DEFAULT_LANGUAGE);
      return of(<Result>{ isSuccess: true });
    } else {
      return this._api.User_UpdateLanguageCulture(languageCulture?.idLanguageCulture!)
        .pipe(
          catchError((err, obs) => {
            const message = `Language - Error during update language. Current language was set to default language ${environment.DEFAULT_LANGUAGE}`
            this._currentLanguageSubject.next(language ?? null)
            this._translate.use(environment.DEFAULT_LANGUAGE);
            console.error(message);
            return throwError(() => new Error(message));
          })
        );
    }
  }

  private _findLanguageCulture(languageCultureName: string) {
    return this._supportedLanguages.find(x => x.languageCultureName == languageCultureName);
  }

  private _setRegex(languageCulture: LanguageCulture) {
    this.regex = languageCulture.languageCultureName == 'it-IT' ? /^[0-9]+|\,$/g : /^[0-9]+|\.$/g;
    this.regexSeparator = languageCulture.languageCultureName == 'it-IT' ? /\,/g : /\./g;

    this._decimalSeparator = languageCulture.languageCultureName == 'it-IT' ? this._decimalSeparatorIt : this._decimalSeparatorEn;
    this._thousandSeparator = languageCulture.languageCultureName == 'it-IT' ? this._thousandSeparatorIt : this._thousandSeparatorEn;
  }

}
